import { Stack, Typography, useTheme } from "@mui/material";
import { useDashboardScope } from "../../hooks/useDashboardScope";
import type { OverviewPeriod } from "../../types";
import { PeriodMenu } from "./PeriodMenu";

const PERIOD_OPTIONS: Array<{ label: string; value: OverviewPeriod }> = [
  { label: "Current", value: "all" },
  { label: "Today", value: "today" },
  { label: "This week", value: "last_week" },
  { label: "This month", value: "last_month" },
  { label: "Last month", value: "previous_month" },
];

interface OverviewHeaderProps {
  title: string;
  period: OverviewPeriod;
  onPeriodChange: (period: OverviewPeriod) => void;
}

export const OverviewHeader = ({
  title,
  period,
  onPeriodChange,
}: OverviewHeaderProps) => {
  const theme = useTheme();
  const { mode, resources } = useDashboardScope();

  // Whose numbers the cards below are showing. One teammate gets named; the
  // whole team or any multi-select collapses to a single generic heading,
  // because listing five names doesn't fit.
  const isSingleResource = mode !== "all" && resources.length === 1;

  const scopeLabel = isSingleResource
    ? `${resources[0].firstName} ${resources[0].lastName}`.trim()
    : mode === "all" || resources.length > 1
      ? "Team overview"
      : "";

  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", justifyContent: "space-between" }}
    >
      {/* Scoped past "Me" the section is no longer yours, so the heading says
          whose work it is — styled identically to the plain "Overview" title. */}
      <Typography
        variant="subtitle1"
        sx={{
          color: theme.palette.lightBg.high,
          // Only names need normalising; the static titles are already cased.
          textTransform: isSingleResource ? "capitalize" : "none",
        }}
      >
        {scopeLabel || title}
      </Typography>

      <PeriodMenu
        options={PERIOD_OPTIONS}
        value={period}
        onChange={onPeriodChange}
        ariaLabel={`${title} period`}
      />
    </Stack>
  );
};
